import React, { useEffect, useMemo, useState } from "react";
import { Tooltip } from "antd";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { getNavigationIcon } from "./IconRegistry";
import "./SidebarNavigation.css";

const PINNED_STORAGE_KEY = "marinebytes.sidebar.pinned";
const RECENT_STORAGE_KEY = "marinebytes.sidebar.recent";
const RECENT_LIMIT = 5;

const readStoredKeys = (key) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const sortByNavigationOrder = (items = []) =>
  [...items]
    .filter((item) => item && item.active !== false)
    .sort(
      (left, right) =>
        (Number(left.navigation_order) || 0) -
        (Number(right.navigation_order) || 0),
    )
    .map((item) => ({
      ...item,
      children: sortByNavigationOrder(item.children || []),
    }));

const getModuleKey = (module) => module.module_key;
const isNavigable = (module) =>
  Boolean(module.path) && module.type !== "container";
const matchesRoute = (path, currentPath) =>
  Boolean(path && path === currentPath);

const toLabel = (value) =>
  String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const flattenModules = (modules) =>
  modules.flatMap((module) => [
    module,
    ...flattenModules(module.children || []),
  ]);

const containsActiveRoute = (module, currentPath) =>
  matchesRoute(module.path, currentPath) ||
  (module.children || []).some((child) =>
    containsActiveRoute(child, currentPath),
  );

const collectActiveContainers = (modules, currentPath) =>
  modules.reduce((keys, module) => {
    if (module.children?.length && containsActiveRoute(module, currentPath)) {
      keys.push(getModuleKey(module));
      keys.push(...collectActiveContainers(module.children, currentPath));
    }
    return keys;
  }, []);

const filterModules = (modules, query) => {
  const normalisedQuery = query.trim().toLocaleLowerCase();
  if (!normalisedQuery) return modules;

  return modules.reduce((matches, module) => {
    const matchingChildren = filterModules(module.children || [], query);
    const matchesModuleName = module.name
      ?.toLocaleLowerCase()
      .includes(normalisedQuery);

    if (matchesModuleName || matchingChildren.length) {
      matches.push({
        ...module,
        children: matchesModuleName ? module.children || [] : matchingChildren,
      });
    }
    return matches;
  }, []);
};

const groupByDomainAndSection = (modules) => {
  const domains = new Map();

  modules.forEach((module) => {
    const domainKey = module.domain || "unclassified";
    if (!domains.has(domainKey)) domains.set(domainKey, []);
    domains.get(domainKey).push(module);
  });

  return [...domains.entries()]
    .map(([domain, domainModules]) => {
      const sections = new Map();
      domainModules.forEach((module) => {
        const sectionKey = module.section || "";
        if (!sections.has(sectionKey)) sections.set(sectionKey, []);
        sections.get(sectionKey).push(module);
      });

      return {
        domain,
        order: Math.min(
          ...domainModules.map(
            (module) => Number(module.navigation_order) || 0,
          ),
        ),
        sections: [...sections.entries()]
          .map(([section, sectionModules]) => ({
            section,
            order: Math.min(
              ...sectionModules.map(
                (module) => Number(module.navigation_order) || 0,
              ),
            ),
            modules: sectionModules,
          }))
          .sort((left, right) => left.order - right.order),
      };
    })
    .sort((left, right) => left.order - right.order);
};

const SidebarItem = ({
  module,
  depth,
  expandedKeys,
  onToggle,
  onNavigate,
  onTogglePin,
  pinnedKeys,
  collapsed,
  expandAll,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const children = module.children || [];
  const hasChildren = children.length > 0 && module.is_container !== false;
  const isExpandable = hasChildren && module.expandable !== false;
  const canNavigate = isNavigable(module);
  const moduleKey = getModuleKey(module);
  const isPinned = pinnedKeys.includes(moduleKey);
  const isExpanded = expandAll || expandedKeys.has(moduleKey);
  const isActive = matchesRoute(module.path, location.pathname);
  const Icon = getNavigationIcon(module.icon);

  const selectModule = () => {
    if (canNavigate) {
      onNavigate(module);
      navigate(module.path);
    } else if (isExpandable) {
      onToggle(moduleKey);
    }
  };

  const item = (
    <div className="sidebar-navigation__item-wrap">
      <div className="sidebar-navigation__item-row">
        <button
          type="button"
          className={`sidebar-navigation__item ${
            isActive ? "sidebar-navigation__item--active" : ""
          }`}
          style={{ "--sidebar-depth": depth }}
          onClick={selectModule}
          aria-current={isActive ? "page" : undefined}
          aria-expanded={isExpandable ? isExpanded : undefined}
        >
          <Icon className="sidebar-navigation__icon" />
          <span className="sidebar-navigation__item-name">{module.name}</span>
        </button>
        {!collapsed && (canNavigate || isExpandable) && (
          <span className="sidebar-navigation__item-actions">
            {canNavigate && (
              <button
                type="button"
                className={`sidebar-navigation__action ${
                  isPinned ? "sidebar-navigation__action--pinned" : ""
                }`}
                onClick={() => onTogglePin(moduleKey)}
                aria-label={`${isPinned ? "Unpin" : "Pin"} ${module.name}`}
              >
                {isPinned ? <StarFilled /> : <StarOutlined />}
              </button>
            )}
            {isExpandable && (
              <button
                type="button"
                className="sidebar-navigation__action"
                onClick={() => onToggle(moduleKey)}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${module.name}`}
              >
                {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </button>
            )}
          </span>
        )}
      </div>

      {hasChildren && (
        <div
          className={`sidebar-navigation__children ${
            isExpanded ? "sidebar-navigation__children--expanded" : ""
          }`}
        >
          <div className="sidebar-navigation__children-inner">
            {children.map((child) => (
              <SidebarItem
                key={getModuleKey(child)}
                module={child}
                depth={depth + 1}
                expandedKeys={expandedKeys}
                onToggle={onToggle}
                onNavigate={onNavigate}
                onTogglePin={onTogglePin}
                pinnedKeys={pinnedKeys}
                collapsed={collapsed}
                expandAll={expandAll}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return collapsed ? (
    <Tooltip title={module.name} placement="right">
      {item}
    </Tooltip>
  ) : (
    item
  );
};

const SidebarShortcut = ({ module, pinned, onNavigate, onTogglePin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const Icon = getNavigationIcon(module.icon);
  const isActive = matchesRoute(module.path, location.pathname);

  return (
    <div className="sidebar-navigation__shortcut-row">
      <button
        type="button"
        className={`sidebar-navigation__shortcut ${
          isActive ? "sidebar-navigation__shortcut--active" : ""
        }`}
        onClick={() => {
          onNavigate(module);
          navigate(module.path);
        }}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon />
        <span>{module.name}</span>
      </button>
      <button
        type="button"
        className={`sidebar-navigation__action ${
          pinned ? "sidebar-navigation__action--pinned" : ""
        }`}
        onClick={() => onTogglePin(getModuleKey(module))}
        aria-label={`${pinned ? "Unpin" : "Pin"} ${module.name}`}
      >
        {pinned ? <StarFilled /> : <StarOutlined />}
      </button>
    </div>
  );
};

const SidebarNavigation = ({ modules = [], collapsed = false }) => {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [pinnedKeys, setPinnedKeys] = useState(() =>
    readStoredKeys(PINNED_STORAGE_KEY),
  );
  const [recentKeys, setRecentKeys] = useState(() =>
    readStoredKeys(RECENT_STORAGE_KEY),
  );
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [expandedDomain, setExpandedDomain] = useState(null);
  const sortedModules = useMemo(
    () => sortByNavigationOrder(modules),
    [modules],
  );
  const filteredModules = useMemo(
    () => filterModules(sortedModules, search),
    [search, sortedModules],
  );
  const domains = useMemo(
    () => groupByDomainAndSection(filteredModules),
    [filteredModules],
  );
  const moduleByKey = useMemo(
    () =>
      new Map(
        flattenModules(sortedModules).map((module) => [
          getModuleKey(module),
          module,
        ]),
      ),
    [sortedModules],
  );
  const pinnedModules = pinnedKeys
    .map((moduleKey) => moduleByKey.get(moduleKey))
    .filter((module) => module && isNavigable(module))
    .filter((module) =>
      module.name
        ?.toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase()),
    );
  const recentModules = recentKeys
    .map((moduleKey) => moduleByKey.get(moduleKey))
    .filter((module) => module && isNavigable(module))
    .filter((module) =>
      module.name
        ?.toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase()),
    );
  const isSearching = Boolean(search.trim());

  useEffect(() => {
    window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinnedKeys));
  }, [pinnedKeys]);

  useEffect(() => {
    window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recentKeys));
  }, [recentKeys]);

  useEffect(() => {
    const activeContainers = collectActiveContainers(
      sortedModules,
      location.pathname,
    );
    if (activeContainers.length) {
      setExpandedKeys(
        (currentKeys) => new Set([...currentKeys, ...activeContainers]),
      );
    }

    const activeDomain = sortedModules.find((module) =>
      containsActiveRoute(module, location.pathname),
    )?.domain;
    if (activeDomain) setExpandedDomain(activeDomain);
  }, [location.pathname, sortedModules]);

  const toggleModule = (moduleKey) => {
    setExpandedKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      if (nextKeys.has(moduleKey)) nextKeys.delete(moduleKey);
      else nextKeys.add(moduleKey);
      return nextKeys;
    });
  };

  const togglePin = (moduleKey) => {
    setPinnedKeys((currentKeys) =>
      currentKeys.includes(moduleKey)
        ? currentKeys.filter((key) => key !== moduleKey)
        : [moduleKey, ...currentKeys],
    );
  };

  const recordNavigation = (module) => {
    const moduleKey = getModuleKey(module);
    setRecentKeys((currentKeys) =>
      [moduleKey, ...currentKeys.filter((key) => key !== moduleKey)].slice(
        0,
        RECENT_LIMIT,
      ),
    );
  };

  if (!sortedModules.length) return null;

  return (
    <nav
      className={`sidebar-navigation ${
        collapsed ? "sidebar-navigation--collapsed" : ""
      }`}
      aria-label="Application navigation"
    >
      {!collapsed && (
        <label className="sidebar-navigation__search">
          <SearchOutlined aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search modules..."
            aria-label="Search navigation"
          />
        </label>
      )}

      {!collapsed && (
        <div className="sidebar-navigation__shortcuts">
          <div className="sidebar-navigation__shortcut-heading">
            <StarOutlined /> Favorites
          </div>
          {pinnedModules.length ? (
            pinnedModules.map((module) => (
              <SidebarShortcut
                key={getModuleKey(module)}
                module={module}
                pinned
                onNavigate={recordNavigation}
                onTogglePin={togglePin}
              />
            ))
          ) : (
            <p className="sidebar-navigation__empty">
              Pin modules for quick access
            </p>
          )}

          <div className="sidebar-navigation__shortcut-heading">
            <ClockCircleOutlined /> Recent
          </div>
          {recentModules.length ? (
            recentModules.map((module) => (
              <SidebarShortcut
                key={getModuleKey(module)}
                module={module}
                pinned={pinnedKeys.includes(getModuleKey(module))}
                onNavigate={recordNavigation}
                onTogglePin={togglePin}
              />
            ))
          ) : (
            <p className="sidebar-navigation__empty">
              Recently opened modules appear here
            </p>
          )}
        </div>
      )}

      <div className="sidebar-navigation__domains">
        {domains.map(({ domain, sections }) => {
          const rootModules = sections.flatMap((section) => section.modules);
          const standaloneModule =
            sections.length === 1 &&
            !sections[0].section &&
            rootModules.length === 1 &&
            isNavigable(rootModules[0]) &&
            !rootModules[0].is_container
              ? rootModules[0]
              : null;
          const isOpen = collapsed || isSearching || expandedDomain === domain;

          if (standaloneModule) {
            return (
              <SidebarItem
                key={getModuleKey(standaloneModule)}
                module={standaloneModule}
                depth={0}
                expandedKeys={expandedKeys}
                onToggle={toggleModule}
                onNavigate={recordNavigation}
                onTogglePin={togglePin}
                pinnedKeys={pinnedKeys}
                collapsed={collapsed}
                expandAll={isSearching}
              />
            );
          }

          return (
            <section className="sidebar-navigation__domain" key={domain}>
              <button
                type="button"
                className="sidebar-navigation__domain-toggle"
                onClick={() =>
                  setExpandedDomain((currentDomain) =>
                    currentDomain === domain ? null : domain,
                  )
                }
                aria-expanded={isOpen}
              >
                <span className="sidebar-navigation__domain-label">
                  {toLabel(domain)}
                </span>
                {isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </button>
              <div
                className={`sidebar-navigation__domain-content ${
                  isOpen ? "sidebar-navigation__domain-content--expanded" : ""
                }`}
              >
                <div className="sidebar-navigation__domain-content-inner">
                  {sections.map(({ section, modules: sectionModules }) => (
                    <div
                      className="sidebar-navigation__section"
                      key={section || "root"}
                    >
                      {section && (
                        <h4 className="sidebar-navigation__section-label">
                          {toLabel(section)}
                        </h4>
                      )}
                      {sectionModules.map((module) => (
                        <SidebarItem
                          key={getModuleKey(module)}
                          module={module}
                          depth={0}
                          expandedKeys={expandedKeys}
                          onToggle={toggleModule}
                          onNavigate={recordNavigation}
                          onTogglePin={togglePin}
                          pinnedKeys={pinnedKeys}
                          collapsed={collapsed}
                          expandAll={isSearching}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </nav>
  );
};

export default SidebarNavigation;
